import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

export interface BackgroundRemovalResult {
  success: boolean;
  imageUrl?: string;
  imageData?: string; // Base64 encoded image
  error?: string;
}

@Injectable({
  providedIn: 'root'
})
export class BackgroundRemovalService {
  private readonly apiUrl = `${environment.BASE_API_PATH}/ArtistProduct/remove-background`;
  private readonly TOKEN_KEY = 'app_access_token';

  // Compression settings - target ~2MB to avoid 413 errors while preserving quality
  private readonly MAX_FILE_SIZE_MB = 2.0;
  private readonly MAX_DIMENSION = 3000; // Max width or height in pixels (higher to preserve resolution)
  private readonly JPEG_QUALITY = 0.92; // Higher quality JPEG compression

  constructor(private http: HttpClient) {}

  /**
   * Get the access token from localStorage
   */
  private getAccessToken(): string {
    return localStorage.getItem(this.TOKEN_KEY) || '';
  }

  /**
   * Remove background from an image file
   * @param file The image file to process
   * @returns Observable with the processed image
   */
  removeBackground(file: File): Observable<Blob> {
    const formData = new FormData();
    formData.append('file', file);

    const token = this.getAccessToken();
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });

    return this.http.post(this.apiUrl, formData, {
      headers: headers,
      responseType: 'blob'
    });
  }

  /**
   * Remove background from a canvas element
   * @param canvas The canvas containing the image
   * @param filename Optional filename for the request
   * @returns Promise with the processed image as HTMLImageElement
   */
  async removeBackgroundFromCanvas(
    canvas: HTMLCanvasElement,
    filename: string = 'image.png'
  ): Promise<HTMLImageElement> {
    // Compress the canvas if needed to stay under file size limit
    const { blob, wasCompressed } = await this.compressCanvasForUpload(canvas);

    if (wasCompressed) {
      console.log(`Image compressed for upload: ${(blob.size / 1024 / 1024).toFixed(2)}MB`);
    }

    // Create File from Blob - use appropriate extension based on compression
    const finalFilename = wasCompressed ? filename.replace(/\.\w+$/, '.jpg') : filename;
    const file = new File([blob], finalFilename, { type: blob.type });

    // Send to API
    const resultBlob = await this.removeBackground(file).toPromise();

    if (!resultBlob) {
      throw new Error('No response from background removal API');
    }

    // Convert result blob to image
    return this.blobToImage(resultBlob);
  }

  /**
   * Compress a canvas image for upload, targeting MAX_FILE_SIZE_MB
   * Returns the blob and whether compression was applied
   */
  private async compressCanvasForUpload(canvas: HTMLCanvasElement): Promise<{ blob: Blob; wasCompressed: boolean }> {
    const maxBytes = this.MAX_FILE_SIZE_MB * 1024 * 1024;

    // First, try PNG at original size
    let blob = await this.canvasToBlob(canvas, 'image/png', 1.0);

    if (blob.size <= maxBytes) {
      return { blob, wasCompressed: false };
    }

    console.log(`Original image size: ${(blob.size / 1024 / 1024).toFixed(2)}MB - compressing...`);

    // Need to compress - first resize if dimensions are too large
    let workingCanvas = canvas;
    const needsResize = canvas.width > this.MAX_DIMENSION || canvas.height > this.MAX_DIMENSION;

    if (needsResize) {
      workingCanvas = this.resizeCanvas(canvas, this.MAX_DIMENSION);
      console.log(`Resized from ${canvas.width}x${canvas.height} to ${workingCanvas.width}x${workingCanvas.height}`);
    }

    // Try JPEG at high quality first
    blob = await this.canvasToBlob(workingCanvas, 'image/jpeg', this.JPEG_QUALITY);

    if (blob.size <= maxBytes) {
      return { blob, wasCompressed: true };
    }

    // Still too large - progressively reduce quality (but keep above 0.75 for good quality)
    let quality = 0.88;
    while (blob.size > maxBytes && quality > 0.75) {
      quality -= 0.04; // Smaller steps for finer control
      blob = await this.canvasToBlob(workingCanvas, 'image/jpeg', quality);
      console.log(`Trying quality ${quality.toFixed(2)}: ${(blob.size / 1024 / 1024).toFixed(2)}MB`);
    }

    // If still too large, reduce dimensions (prefer dimension reduction over quality loss)
    if (blob.size > maxBytes) {
      const scaleFactor = Math.sqrt(maxBytes / blob.size) * 0.95; // 5% safety margin
      const newMaxDim = Math.floor(Math.max(workingCanvas.width, workingCanvas.height) * scaleFactor);
      workingCanvas = this.resizeCanvas(workingCanvas, newMaxDim);
      blob = await this.canvasToBlob(workingCanvas, 'image/jpeg', 0.85); // Still good quality
      console.log(`Resized to ${workingCanvas.width}x${workingCanvas.height}: ${(blob.size / 1024 / 1024).toFixed(2)}MB`);
    }

    return { blob, wasCompressed: true };
  }

  /**
   * Resize canvas to fit within maxDimension while maintaining aspect ratio
   */
  private resizeCanvas(canvas: HTMLCanvasElement, maxDimension: number): HTMLCanvasElement {
    const { width, height } = canvas;

    // Calculate new dimensions
    let newWidth = width;
    let newHeight = height;

    if (width > height) {
      if (width > maxDimension) {
        newWidth = maxDimension;
        newHeight = Math.round(height * (maxDimension / width));
      }
    } else {
      if (height > maxDimension) {
        newHeight = maxDimension;
        newWidth = Math.round(width * (maxDimension / height));
      }
    }

    // Create resized canvas
    const resizedCanvas = document.createElement('canvas');
    resizedCanvas.width = newWidth;
    resizedCanvas.height = newHeight;

    const ctx = resizedCanvas.getContext('2d');
    if (ctx) {
      // Use high-quality image smoothing
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(canvas, 0, 0, newWidth, newHeight);
    }

    return resizedCanvas;
  }

  /**
   * Convert canvas to blob with specified format and quality
   */
  private canvasToBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
    return new Promise((resolve, reject) => {
      canvas.toBlob(
        (b) => {
          if (b) resolve(b);
          else reject(new Error('Failed to convert canvas to blob'));
        },
        type,
        quality
      );
    });
  }

  /**
   * Remove background from an HTMLImageElement
   * @param image The image element to process
   * @returns Promise with the processed image as HTMLImageElement
   */
  async removeBackgroundFromImage(image: HTMLImageElement): Promise<HTMLImageElement> {
    // Create a canvas from the image
    const canvas = document.createElement('canvas');
    canvas.width = image.width;
    canvas.height = image.height;
    const ctx = canvas.getContext('2d');

    if (!ctx) {
      throw new Error('Could not create canvas context');
    }

    ctx.drawImage(image, 0, 0);

    return this.removeBackgroundFromCanvas(canvas, 'artwork.png');
  }

  /**
   * Convert a Blob to HTMLImageElement
   */
  private blobToImage(blob: Blob): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const url = URL.createObjectURL(blob);
      const img = new Image();

      img.onload = () => {
        URL.revokeObjectURL(url);
        resolve(img);
      };

      img.onerror = () => {
        URL.revokeObjectURL(url);
        reject(new Error('Failed to load processed image'));
      };

      img.src = url;
    });
  }

  /**
   * Apply alpha erosion to remove white fringe from edges
   * This helps clean up artifacts from AI background removal
   */
  applyAlphaErosion(canvas: HTMLCanvasElement, erosionPx: number = 1): void {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const width = canvas.width;
    const height = canvas.height;

    // Create a copy for reading
    const copy = new Uint8ClampedArray(data);

    // Erode alpha channel
    for (let y = erosionPx; y < height - erosionPx; y++) {
      for (let x = erosionPx; x < width - erosionPx; x++) {
        const idx = (y * width + x) * 4;
        let minAlpha = 255;

        // Check neighboring pixels
        for (let dy = -erosionPx; dy <= erosionPx; dy++) {
          for (let dx = -erosionPx; dx <= erosionPx; dx++) {
            const nIdx = ((y + dy) * width + (x + dx)) * 4 + 3;
            minAlpha = Math.min(minAlpha, copy[nIdx]);
          }
        }

        // Set alpha to minimum of neighbors (erosion effect)
        data[idx + 3] = minAlpha;
      }
    }

    ctx.putImageData(imageData, 0, 0);
  }

  /**
   * Crop canvas to visible (non-transparent) content
   */
  cropToVisible(canvas: HTMLCanvasElement, padding: number = 0): HTMLCanvasElement {
    const ctx = canvas.getContext('2d');
    if (!ctx) return canvas;

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const data = imageData.data;
    const width = canvas.width;
    const height = canvas.height;

    let minX = width, minY = height, maxX = 0, maxY = 0;

    // Find bounds of non-transparent pixels
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const alpha = data[(y * width + x) * 4 + 3];
        if (alpha > 0) {
          minX = Math.min(minX, x);
          minY = Math.min(minY, y);
          maxX = Math.max(maxX, x);
          maxY = Math.max(maxY, y);
        }
      }
    }

    // Add padding
    minX = Math.max(0, minX - padding);
    minY = Math.max(0, minY - padding);
    maxX = Math.min(width - 1, maxX + padding);
    maxY = Math.min(height - 1, maxY + padding);

    // Create cropped canvas
    const croppedWidth = maxX - minX + 1;
    const croppedHeight = maxY - minY + 1;

    const croppedCanvas = document.createElement('canvas');
    croppedCanvas.width = croppedWidth;
    croppedCanvas.height = croppedHeight;

    const croppedCtx = croppedCanvas.getContext('2d');
    if (croppedCtx) {
      croppedCtx.drawImage(
        canvas,
        minX, minY, croppedWidth, croppedHeight,
        0, 0, croppedWidth, croppedHeight
      );
    }

    return croppedCanvas;
  }
}
